import { Button, Modal, RsInput, Table } from '@/components/Bicester';
import { PopoverWrapper } from '@/components/Bicester/PopoverWrapper';
import { Cell } from '@/components/Bicester/Table';
import { DateTimeCell, LevelCell, UserNameCell } from '@/components/Bicester/Table/Cells';
import PageLoader from '@/components/shared/PageLoader';
import { BICESTER_FULL_DATE_HOURS_MINUTES } from '@/constants/dateFormat';
import { SERVICE_STATUS } from '@/services/constants';
import { fetchRepairTicket } from '@/services/exchange';
import { useRequest } from 'ahooks';
import React, { useState } from 'react';
import { Alert } from 'rsuite';

const ExchangedTicketTable = ({
  data,
  page,
  total,
  onChange,
  onReSearch,
  loading,
}: {
  data: any[];
  page: number;
  total: number;
  onChange: (params: any) => void;
  onReSearch?: () => void;
  loading?: boolean;
}) => {
  const defaultModal = {
    show: false,
    id: null,
    ticketId: '',
    remarkCode: '',
  };
  const [showModal, setShowModal] = useState(defaultModal);

  const { run: repairTicket, loading: repairLoading } = useRequest(fetchRepairTicket, {
    manual: true,
  });

  return (
    <>
      {repairLoading && <PageLoader />}
      <Table
        pageInfo={{ page, total }}
        data={data}
        loading={loading}
        columns={[
          {
            columnProps: {
              flexGrow: 1.9,
            },
            header: '卡券号',
            cell: <Cell dataKey="ticket_id" />,
          },
          {
            columnProps: {
              flexGrow: 0.8,
            },
            header: '客户名',
            cell: <UserNameCell dataKey="user_name" />,
          },
          {
            columnProps: {
              flexGrow: 1.4,
            },
            header: '手机号',
            cell: <Cell dataKey="phone" />,
          },
          {
            columnProps: { flexGrow: 1 },
            header: '会员等级',
            cell: <LevelCell dataKey="level" />,
          },
          {
            columnProps: {
              flexGrow: 3.5,
            },
            header: '卡券名/卡券所属活动',
            cell: (
              <Cell>
                {(rowData) => (
                  <PopoverWrapper
                    popover={{
                      content: (
                        <div className="text-[#455255]">
                          {rowData?.coupons_name && <div>{rowData?.coupons_name}</div>}
                          {rowData?.ticket_act_name && <div>活动：{rowData?.ticket_act_name}</div>}
                        </div>
                      ),
                    }}
                  >
                    <div className="multiline-cell-content-wrap">
                      {rowData?.coupons_name && (
                        <div className="multiline-cell-content">{rowData?.coupons_name}</div>
                      )}
                      {rowData?.ticket_act_name && (
                        <div className="multiline-cell-content">
                          活动：{rowData?.ticket_act_name}
                        </div>
                      )}
                    </div>
                  </PopoverWrapper>
                )}
              </Cell>
            ),
          },
          {
            columnProps: {
              flexGrow: 1.7,
            },
            header: '卡券有效期',
            cell: (
              <Cell>
                {(rowData) => (
                  <div className="multiline-cell-content-wrap">
                    <div className="multiline-cell-content">开始{rowData?.start_at ?? '--'}</div>
                    <div className="multiline-cell-content">结束{rowData?.end_at ?? '--'}</div>
                  </div>
                )}
              </Cell>
            ),
          },
          {
            columnProps: {
              flexGrow: 2,
            },
            header: '备用券备注',
            cell: (
              <Cell>
                {(rowData) => {
                  // is_repair 是否显示补券按钮 0显示1不显示 只有已兑换的才有这个字段
                  return rowData?.is_repair ? (
                    <div className="multiline-cell-content-wrap">
                      {rowData?.repair_extra && (
                        <div className="multiline-cell-content">{rowData?.repair_extra}</div>
                      )}
                      {rowData?.repair_creator_name && (
                        <div className="multiline-cell-content">
                          补券人 {rowData?.repair_creator_name}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Button
                      appearance="link"
                      onClick={() =>
                        setShowModal({
                          ...showModal,
                          show: true,
                          id: rowData?.id,
                          ticketId: rowData?.ticket_id,
                        })
                      }
                    >
                      备用券备注
                    </Button>
                  );
                }}
              </Cell>
            ),
          },
          {
            columnProps: {
              flexGrow: 2,
            },
            header: '兑换时间',
            cell: <DateTimeCell dataKey="gift_time" format={BICESTER_FULL_DATE_HOURS_MINUTES} />,
          },
          {
            columnProps: {
              flexGrow: 2,
            },
            header: '兑换人/备注',
            cell: (
              <Cell>
                {(rowData) => (
                  <PopoverWrapper
                    popover={{
                      content: (
                        <>
                          {rowData?.gift_member_name && (
                            <div>兑换人 {rowData?.gift_member_name}</div>
                          )}
                          {rowData?.extra && <div>{rowData?.extra}</div>}
                        </>
                      ),
                    }}
                  >
                    <div className="multiline-cell-content-wrap">
                      {rowData?.gift_member_name && (
                        <div className="multiline-cell-content">
                          兑换人 {rowData?.gift_member_name}
                        </div>
                      )}
                      {rowData?.extra && (
                        <div className="multiline-cell-content">{rowData?.extra}</div>
                      )}
                    </div>
                  </PopoverWrapper>
                )}
              </Cell>
            ),
          },
        ]}
        onChange={onChange}
      >
        <Table.Body height={289} rowHeight={64} />
        <Table.Pagination style={{ ...(total === 0 && { visibility: 'hidden' }) }} />
      </Table>
      <Modal
        show={showModal.show}
        isInput
        content={
          <>
            <div className="flex justify-start h-[30px] leading-[30px] mb-[20px]">
              卡券号：{showModal.ticketId}
            </div>
            <RsInput
              className="h-[30px]"
              placeholder="请备注补券号"
              value={showModal.remarkCode}
              onChange={(remarkCode) => setShowModal({ ...showModal, remarkCode })}
            />
          </>
        }
        confirm="确认补券"
        cancel="取消"
        onConfirm={() => {
          repairTicket({
            id: showModal.id,
            extra: showModal.remarkCode,
          }).then((res) => {
            if (res?.status === SERVICE_STATUS.SUCCESS) {
              Alert.success('补券成功');
              onReSearch();
            }
          });
          setShowModal(defaultModal);
        }}
        onCancel={() => setShowModal(defaultModal)}
      />
    </>
  );
};

export default ExchangedTicketTable;
